import React, { useState, useEffect } from "react";
import { Button, Container, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTimes, faCopy } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/appContext";
import { isViewOnly } from "../../utils/permissions";
import { Paginate } from "../../components";
import BulkFeatureAssignmentModal from "../FeatureConfig/BulkFeatureAssignmentModal";
import { DeleteTenantModal, CopyTenantConfigurationModal } from "./UpdateTenant";

const noop = () => {};

const TenantsListPage = ({
  env,
  tenants,
  allTenants,
  isLoading,
  isError,
  pageResetFlag,
  subscriptions,
  handleEnvChange,
  users,
  selectedTenantIds = [],
  onSelectedTenantIdsChange = noop,
  showBulkAssignModal = false,
  onOpenBulkAssignModal = noop,
  onCloseBulkAssignModal = noop
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const { user } = useAppContext();
  const viewOnly = isViewOnly(user);

  const itemsPerPage = 20;
  const indexOfLastTenant = currentPage * itemsPerPage;
  const indexOfFirstTenant = indexOfLastTenant - itemsPerPage;
  const currentTenants = tenants?.slice(indexOfFirstTenant, indexOfLastTenant);
  const visibleTenantIds = tenants?.map((tenant) => tenant.id) || [];
  const allTenantsSelected = visibleTenantIds.length > 0 && visibleTenantIds.every((tenantId) => selectedTenantIds.includes(tenantId));

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = tenants?.length ? Math.ceil(tenants?.length / itemsPerPage) : 0;

  const handleEditClick = (tenant) => {
    const path = viewOnly ? `/view-tenant/${tenant.id}` : `/edit-tenant/${tenant.id}`;
    navigate(path, { state: { tenant } });
  };

  const handleDeleteClick = (tenant) => {
    handleEnvChange(env, tenant.id, tenant.name);
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  const handleCopyClick = (tenant) => {
    setSelectedTenant(tenant);
    setShowCopyModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedTenant(null);
  };

  const handleCloseCopyModal = () => {
    setShowCopyModal(false);
    setSelectedTenant(null);
  };

  const toggleTenantSelection = (tenantId) => {
    onSelectedTenantIdsChange(
      selectedTenantIds.includes(tenantId)
        ? selectedTenantIds.filter((selectedId) => selectedId !== tenantId)
        : [...selectedTenantIds, tenantId]
    );
  };

  const toggleAllTenantsSelection = () => {
    if (allTenantsSelected) {
      onSelectedTenantIdsChange(selectedTenantIds.filter((tenantId) => !visibleTenantIds.includes(tenantId)));
      return;
    }
    onSelectedTenantIdsChange([...new Set([...selectedTenantIds, ...visibleTenantIds])]);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [pageResetFlag, tenants]);

  useEffect(() => {
    const availableTenantIds = new Set((allTenants || []).map((tenant) => tenant.id));
    const availableSelectedIds = selectedTenantIds.filter((tenantId) => availableTenantIds.has(tenantId));
    if (availableSelectedIds.length !== selectedTenantIds.length) {
      onSelectedTenantIdsChange(availableSelectedIds);
    }
  }, [allTenants, onSelectedTenantIdsChange, selectedTenantIds]);

  return (
    <Container fluid>
      {!viewOnly && selectedTenantIds.length > 0 && !isLoading && !isError && (
        <div className="d-flex align-items-center gap-3 mb-3">
          <span className="text-muted">
            {selectedTenantIds.length} tenant{selectedTenantIds.length === 1 ? "" : "s"} selected
          </span>
          <Button variant="primary" size="sm" onClick={onOpenBulkAssignModal}>
            Bulk Assign Features
          </Button>
        </div>
      )}

      {!isLoading && !isError && (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              {!viewOnly && (
                <th scope="col" style={{ width: "48px" }} className="text-center">
                  <Form.Check
                    type="checkbox"
                    aria-label="Select all tenants"
                    checked={allTenantsSelected}
                    onChange={toggleAllTenantsSelection}
                  />
                </th>
              )}
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col">UUID</th>
              <th scope="col">Subscription</th>
              <th scope="col" style={{ width: "80px" }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentTenants?.map((tenant) => (
              <tr key={tenant?.id}>
                {!viewOnly && (
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      aria-label={`Select ${tenant.name}`}
                      checked={selectedTenantIds.includes(tenant.id)}
                      onChange={() => toggleTenantSelection(tenant.id)}
                    />
                  </td>
                )}
                <td>{tenant?.name}</td>
                <td>{tenant?.description}</td>
                <td>{tenant?.id}</td>
                <td>{tenant?.subscription?.name}</td>
                <td>
                  <div className="d-flex justify-content-around align-items-center" style={{ minWidth: "80px" }}>
                    {viewOnly ? (
                      <FontAwesomeIcon icon={faEye} style={{ cursor: "pointer" }} onClick={() => handleEditClick(tenant)} />
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faEdit}
                          style={{ cursor: "pointer", marginRight: "10px" }}
                          onClick={() => handleEditClick(tenant)}
                        />
                        <FontAwesomeIcon
                          icon={faCopy}
                          style={{ cursor: "pointer", marginRight: "10px", color: "#007bff" }}
                          onClick={() => handleCopyClick(tenant)}
                          title="Copy Tenant Configuration"
                        />
                        <FontAwesomeIcon
                          icon={faTimes}
                          style={{ cursor: "pointer", color: "red" }}
                          onClick={() => handleDeleteClick(tenant)}
                        />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {!isLoading && !isError && <Paginate currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />}

      <BulkFeatureAssignmentModal
        show={showBulkAssignModal}
        onHide={onCloseBulkAssignModal}
        onApplied={onSelectedTenantIdsChange}
        tenantIds={selectedTenantIds}
        tenants={allTenants}
      />

      {selectedTenant && (
        <>
          <DeleteTenantModal
            show={showDeleteModal}
            tenant={selectedTenant}
            handleClose={handleCloseDeleteModal}
            handleEnvChange={handleEnvChange}
            env={env}
          />
          <CopyTenantConfigurationModal
            show={showCopyModal}
            sourceTenant={selectedTenant}
            tenants={allTenants}
            handleClose={handleCloseCopyModal}
            handleEnvChange={handleEnvChange}
            env={env}
          />
        </>
      )}
    </Container>
  );
};

export default TenantsListPage;
